import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { Mask } from "../../../types";

/**
 * Per-item configuration + commit callbacks that the centralized
 * <MoveableLayer> needs to drive a single (or grouped) Moveable controller.
 *
 * Each editable item (`DraggableComponent`) registers an entry keyed by its
 * selection key (`item.id ?? item.type`). The layer resolves the selected
 * entries, renders one Moveable targeting their DOM nodes, and on gesture end
 * calls the matching entry's commit callbacks with final pixel geometry
 * (preview-space, measured from the item's bordered box top-left). Each
 * commit closure converts pixels → relative coordinates and calls the item's
 * own `onDragStop`, so all per-item math stays inside `DraggableComponent`.
 */
export type MoveableItemConfig = {
  draggable: boolean;
  resizable: boolean;
  rotatable: boolean;
  keepRatio: boolean;
  /** Moveable resize handle directions, e.g. ["n","s","e","w","nw","ne","sw","se"]. */
  resizeDirections: string[];
  /** Text item with no fixed height — its height auto-fits, never set during resize. */
  autoHeight: boolean;
  /** Whether the Moveable delete control should be offered for this item. */
  canDelete: boolean;
  /** Whether the Moveable duplicate control should be offered for this item. */
  canDuplicate: boolean;
  /** Optional element to constrain dragging within (used by the logo). */
  bounds?: string | Element;
  /**
   * Crop mask of a media item, if any. The layer uses it to constrain dragging
   * so the cropped (visible) area stays inside the canvas while the hidden
   * parts of the media may spill outside.
   */
  mask?: Mask;
};

/** Current pixel geometry of the item's box (preview space), for seeding Moveable. */
export type MoveableFrame = {
  /** Box top-left in preview px (includes the stroke offset). */
  translate: [number, number];
  /** Rotation in degrees (clockwise). */
  rotate: number;
  width: number;
  height: number;
};

export type MoveableEntry = {
  getElement: () => HTMLElement | null;
  getConfig: () => MoveableItemConfig;
  getFrame: () => MoveableFrame;
  commitDrag: (leftPx: number, topPx: number) => void;
  commitResize: (rect: {
    leftPx: number;
    topPx: number;
    widthPx: number;
    heightPx: number;
  }) => void;
  commitRotate: (deg: number) => void;
  /** Delete this item, if it is deletable. */
  onDelete?: () => void;
  /** Duplicate this item, if it supports duplication. */
  onDuplicate?: () => void;
};

type MoveableRegistryValue = {
  register: (key: string, entry: MoveableEntry) => void;
  unregister: (key: string) => void;
  get: (key: string) => MoveableEntry | undefined;
  /** Bumps only when the set of registered keys changes (mount/unmount). */
  version: number;
  /** The layer registers its Moveable.updateRect here so items can ask the
   * control box to re-measure mid-gesture (e.g. when a crop drag moves the
   * item imperatively). */
  setUpdateRect: (fn: (() => void) | null) => void;
  /** Ask the Moveable controls + bounding box to realign to the target now. */
  requestUpdateRect: () => void;
};

const MoveableRegistryContext = createContext<MoveableRegistryValue | null>(
  null
);

export const MoveableRegistryProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const entries = useRef(new Map<string, MoveableEntry>());
  const [version, setVersion] = useState(0);

  const register = useCallback((key: string, entry: MoveableEntry) => {
    const isNew = !entries.current.has(key);
    // Replace the entry in place so the layer always reads fresh closures;
    // only re-render consumers when a key is added (changes the target set).
    entries.current.set(key, entry);
    if (isNew) setVersion((v) => v + 1);
  }, []);

  const unregister = useCallback((key: string) => {
    if (entries.current.delete(key)) setVersion((v) => v + 1);
  }, []);

  const get = useCallback((key: string) => entries.current.get(key), []);

  const updateRectRef = useRef<(() => void) | null>(null);
  const setUpdateRect = useCallback((fn: (() => void) | null) => {
    updateRectRef.current = fn;
  }, []);
  const requestUpdateRect = useCallback(() => updateRectRef.current?.(), []);

  const value = useMemo(
    () => ({
      register,
      unregister,
      get,
      version,
      setUpdateRect,
      requestUpdateRect,
    }),
    [register, unregister, get, version, setUpdateRect, requestUpdateRect]
  );

  return (
    <MoveableRegistryContext.Provider value={value}>
      {children}
    </MoveableRegistryContext.Provider>
  );
};

/**
 * Returns the registry, or `null` when rendered outside a provider (e.g. the
 * read-only preview path), so `DraggableComponent` can no-op its registration.
 */
export const useMoveableRegistry = () => useContext(MoveableRegistryContext);
